import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input } from './FormControls'
import { calculateCables } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function CablesModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:cables:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { cables: [] })
  const initial = superseding ? { cables: superseding.engineering_inputs?.cables || [] } : draft
  const [cables, setCablesRaw] = useState(initial.cables)
  const setCables = (u) => setCablesRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, cables: n })); return n })

  function addCable() { setCables((p) => [...p, { id: `C${Date.now()}`, cableType: '', cableSize: '', numberOfCores: '', routeLength: '', numberOfRuns: '' }]) }
  function updateCable(id, patch) { setCables((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c))) }
  function removeCable(id) { setCables((p) => p.filter((c) => c.id !== id)) }

  const cablesForCalc = useMemo(() => cables.map((c) => ({
    cableType: c.cableType, cableSize: c.cableSize,
    numberOfCores: c.numberOfCores === '' ? null : Number(c.numberOfCores),
    routeLength: c.routeLength === '' ? NaN : Number(c.routeLength),
    numberOfRuns: c.numberOfRuns === '' ? NaN : Number(c.numberOfRuns),
  })), [cables])

  const result = useMemo(() => {
    if (cablesForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateCables(cablesForCalc), error: null } } catch (e) { return { value: null, error: e.message } }
  }, [cablesForCalc])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { cables }
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: 'm', geometrySnapshot: { items: [] }, engineeringInputs, breakdownSnapshot: { steps: result.value.steps, cableBreakdown: result.value.cableBreakdown }, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ padding: '8px 10px', background: '#FFF8E1', border: `1px solid #F5C542`, borderRadius: 6, fontSize: 11, color: '#7A5C00', marginBottom: 14 }}>
        ⚠ Number of Cores is informational only and never multiplies the quantity. Only Number of Runs multiplies Route Length.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Cables</label>
        <Button size="sm" variant="secondary" onClick={addCable}>+ Add Cable</Button>
      </div>
      {cables.map((c) => (
        <div key={c.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Cable Type (e.g. XLPE/SWA)" value={c.cableType} onChange={(e) => updateCable(c.id, { cableType: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeCable(c.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6 }}>
            <L label="Cable Size (descriptive)"><Input value={c.cableSize} onChange={(e) => updateCable(c.id, { cableSize: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            <L label="Number of Cores (informational only)"><Input type="number" value={c.numberOfCores} onChange={(e) => updateCable(c.id, { numberOfCores: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            <L label="Route Length (m)"><Input type="number" value={c.routeLength} onChange={(e) => updateCable(c.id, { routeLength: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
            <L label="Number of Runs (the ONLY multiplier)"><Input type="number" value={c.numberOfRuns} onChange={(e) => updateCable(c.id, { numberOfRuns: e.target.value })} style={{ fontSize: 11, width: '100%' }} /></L>
          </div>
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0' }}>
          {result.value.cableBreakdown.map((cb, i) => (
            <div key={i} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC', fontSize: 11 }}>
              <strong>{cb.cable.cableType}</strong>: {cb.cable.routeLength}m × {cb.cable.numberOfRuns} runs = <strong>{formatNumber(cb.finalQuantity)} m</strong>
            </div>
          ))}
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>Total: {formatNumber(result.value.finalQuantity)} m</div>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>{children}</div> }
